import { Grid, Paper } from "@material-ui/core";
import makeStyles from "@material-ui/core/styles/makeStyles";
import React, { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { ItemState } from "../../redux/state";
import { AppBarButton, AppBarSaveButton } from "@ofqual/portal-components/components/SecondaryAppBar";
import { buildMetaData, getLookupTracker, modelToJsonPatchDocument } from "@ofqual/portal-core/core/utils/modelHelpers";
import { patchingItem } from "../../redux/actions";
import { ModuleAppBar } from "../ModuleAppBar";
import { CommissionModel, CommissionStatus } from "../../domain/CommissionModel";
import { UserType } from "../../redux/types";
import { SectionProps } from "../section/SectionProps";
import { CommissionDetails } from "../section/CommissionDetails";
import { CommissionForecasted } from "../section/CommissionForecasted";
import { useAuth } from "@ofqual/portal-core/core/hooks";
import { authSelector } from "../../redux/selectors";
import { AssignedSpecialist } from "../section/AssignedSpecialist";
import { ExperimentalFeature } from "@ofqual/portal-components/components/ExperimentalFeature";
import { CommissionFinancial } from "../section/CommissionFinancial";
import { AssignedComissionsManager } from "../section/AssignedCommissionsManager";
import { Close, Cancel } from "@material-ui/icons";
import { LockOpen, Restore } from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
  formHeading: {
    color: "#9f9f9f",
    fontSize: "1em",
    marginBottom: "30px",
    fontWeight: 600,
    position: "relative",
  },
  formSection: {
    padding: 30,
    boxShadow: "0 0 19px -2px hsla(0,0%,63.9%,.3)",
    border: "1px solid #ddd",
    backgroundColor: "#fff",
    borderRadius: "3px",
  },
}));

export const ItemDetails: React.FC<{ state: ItemState; }> = ({ state }) => {
  const item = state.item!;
  const dispatch = useDispatch();
  const canEdit = state.status === "LOADED";
  const {
    handleSubmit,
    control,
    formState,
    errors,
    setValue,
    setError,
    clearErrors,
    getValues,
    reset
  } = useForm<CommissionModel>({
    defaultValues: item,
  });
  // this is important for dirtyFields to be deconstructed/accessed prior to the onSubmit process
  const { dirtyFields } = formState;

  const onSubmit = (newModel: CommissionModel) => {
    const changedFields = (Object.keys(
      dirtyFields
    ) as (keyof CommissionModel)[]);
    const document = modelToJsonPatchDocument(newModel, changedFields);
    const meta = buildMetaData(newModel, item, changedFields);

    dispatch(patchingItem(item.id, { document, meta }));
  };

  const [isClosed, setIsClosed] = React.useState(false);
  const [isCancelled, setIsCancelled] = React.useState(false);
  const onSubmitClose = (newModel: CommissionModel) => {

    if (newModel.actualCost == null) {
      setError("actualCost", { type: "custom" });

    }
    if (newModel.actualNumDaysWorked == null) {
      setError("actualNumDaysWorked", { type: "custom" })
    }

    if (newModel.actualCost != null && newModel.actualNumDaysWorked != null) {
      dirtyFields['status'] = true;
      if (isClosed) {
        newModel.status = CommissionStatus["In progress"];
      }
      else {
        newModel.status = CommissionStatus["Closed"];
      }
      onSubmit(newModel);
    }
  };

  const onSubmitCancel = (newModel: CommissionModel) => {
    dirtyFields['status'] = true;
    if(isCancelled) {
      newModel.status = CommissionStatus["In progress"];
    }
    else{
      newModel.status = CommissionStatus["Cancelled"];
    }
    onSubmit(newModel)

  };

  useEffect(() => {
      if (item.status === CommissionStatus["Closed"]){
         setIsClosed(true);
      } else {
        setIsClosed(false); 
      }
      if (item.status === CommissionStatus["Cancelled"]){ 
        setIsCancelled(true);
      } else { 
        setIsCancelled(false);
      }
    
    reset(item)   // clear down the dirty fields
  }, [reset, item]);

  let editable: boolean | undefined = false;
  const { experimentalFeatures } = useAuth(authSelector);

  if (experimentalFeatures !== undefined && experimentalFeatures.find(x => x.moduleName === "portal-commissions"
    && x.featureName === "portal-commissions-editable")) {
    editable = true;
  }

  if (item.status === CommissionStatus["Closed"] || item.status === CommissionStatus["Cancelled"])
  {
    editable = false;    
  }

  const lookupTracker = useMemo(() => getLookupTracker(), []);

  const sectionProps: SectionProps<CommissionModel> = {
    user: { userType: UserType.Unknown, token: "" },
    control,
    errors,
    formState,
    item,
    clearErrors,
    setValue: (name, value) => setValue(name, value),
    getValues,
    registerCustomError: (name: string, isError: boolean) => {
      const isCurrentlyARegisteredError = Object.keys(errors).includes(name);
      if (isError && !isCurrentlyARegisteredError) {
        setError(name as any, {});
      }
      if (!isError && isCurrentlyARegisteredError) {
        clearErrors(name as any);
      }
    },
    editable: editable,
    lookupTracker,
  };

  const classes = useStyles();
  return (
    <>
      <div>
        <ModuleAppBar breadcrumbs={[{ label: `${item.referenceNumber} ${item.title}` }]}>
          {editable ? (<AppBarSaveButton
            onClick={handleSubmit(onSubmit)}
            isSubmitting={state.status === "UPDATING"}
            isSubmitted={state.lastCrudResult.isSuccess}
            disabled={!canEdit}
          />) : null}
          {isClosed ? null : (isCancelled ? (<ExperimentalFeature
            experimentalModuleName="portal-commissions"
            experimentalFeatures={experimentalFeatures}
            experimentalFeatureVisibleName="portal-commissions-cancel">
            <AppBarButton
              onClick={handleSubmit(onSubmitCancel)}
              disabled={!canEdit}
              Icon={Restore}
              data-test="Restore"
              text="Restore"
            />
          </ExperimentalFeature>
          ): (<ExperimentalFeature
          experimentalModuleName="portal-commissions"
          experimentalFeatures={experimentalFeatures}
          experimentalFeatureVisibleName="portal-commissions-cancel">
             <AppBarButton
              onClick={handleSubmit(onSubmitCancel)}
              Icon={Cancel}
              data-test="Cancel"
              text = "Cancel"
            />
          </ExperimentalFeature>
          ))}
          { isCancelled ? null : (isClosed ? (<ExperimentalFeature
            experimentalModuleName="portal-commissions"
            experimentalFeatures={experimentalFeatures}
            experimentalFeatureVisibleName="portal-commissions-close">
            <AppBarButton
              onClick={handleSubmit(onSubmitClose)}
              disabled={!canEdit}
              Icon={LockOpen}
              data-test="Reopen"
              text="ReOpen"
            />
          </ExperimentalFeature>
          ) : (
            <ExperimentalFeature
            experimentalModuleName="portal-commissions"
            experimentalFeatures={experimentalFeatures}
            experimentalFeatureVisibleName="portal-commissions-close">
             <AppBarButton
              onClick={handleSubmit(onSubmitClose)}
              Icon={Close}
              data-test="Close"
              text = "Close"
            />
          </ExperimentalFeature>))}
    
        </ModuleAppBar>

        <h6 className={classes.formHeading} style={{ marginTop: "0" }}>
          Commission Details
        </h6>
        <Paper className={classes.formSection}>
          <Grid>
            <CommissionDetails sectionProps={sectionProps} />
          </Grid>
        </Paper>

        <ExperimentalFeature
          experimentalModuleName="portal-commissions"
          experimentalFeatures={experimentalFeatures}
          experimentalFeatureVisibleName="portal-commissions-forecasted">
          <h6 className={classes.formHeading}>
            Forecasted information
          </h6>

          <Paper className={classes.formSection}>
            <CommissionForecasted sectionProps={sectionProps} />
          </Paper>
        </ExperimentalFeature>

        <h6 className={classes.formHeading} style={{ marginTop: "10" }}>
          Financial Information
        </h6>
        <Paper className={classes.formSection}>
          <Grid>
            <CommissionFinancial sectionProps={sectionProps} />
          </Grid>
        </Paper>

        <h6 className={classes.formHeading}>
          Assigned Managers
        </h6>
        <Paper className={classes.formSection}>
          <Grid>
            <AssignedComissionsManager sectionProps={sectionProps} />
          </Grid>
        </Paper>

        <h6 className={classes.formHeading}>
          Specialist details
        </h6>
        <Paper className={classes.formSection}>
          <AssignedSpecialist sectionProps={sectionProps} />
        </Paper>
      </div>
    </>
  );
};